import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { httpsCallable } from "firebase/functions";
import { doc, onSnapshot } from "firebase/firestore";
import { ChevronLeft, Crown, KeyRound, Loader2, Monitor, Shield, ShieldCheck, Trash2, Copy, CheckCircle2, AlertTriangle } from "lucide-react";
import { functionsAsia, db } from "@/lib/firebase";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useData } from "@/contexts/DataContext";
import type { ActiveDevice, StaffLocks } from "@/lib/planTypes";
import { DEFAULT_STAFF_LOCKS, ULTIMATE_DESKTOP_LIMIT } from "@/lib/planTypes";
import { Switch } from "@/components/ui/switch";
import { logAction } from "@/hooks/use-audit";

type LockKey = keyof StaffLocks;

type LockGroup = { group: string; items: Array<{ key: LockKey; title: string; desc: string; inverted?: boolean }> };

const LOCK_GROUPS: LockGroup[] = [
  {
    group: "Page Access (Hide from staff)",
    items: [
      { key: "dashboard", title: "Hide Dashboard", desc: "Staff cannot see sales KPIs, charts, or overall analytics." },
      { key: "transactions", title: "Hide Transactions", desc: "Staff cannot view transaction history or list." },
      { key: "products", title: "Hide Products", desc: "Staff cannot browse inventory or product grid." },
      { key: "reports", title: "Hide Reports", desc: "Staff cannot access the Reports page." },
      { key: "gstReports", title: "Hide GST Filing", desc: "Staff cannot access the GST Filing page (GSTR-1 + GSTR-3B). On by default — turn off only for trusted staff who handle filing." },
      { key: "settings", title: "Hide Settings Page", desc: "Staff cannot access Settings. Printer + Invoice sub-pages stay visible if allowed." },
    ],
  },
  {
    group: "Actions (Disable operations)",
    items: [
      { key: "exports", title: "Block Data Exports", desc: "Hide PDF/Excel download buttons." },
      { key: "productPricing", title: "Lock Product Prices", desc: "Prevent editing MRP and cost prices." },
      { key: "transactionDelete", title: "Lock Transaction Delete", desc: "Prevent deleting sales or expenses." },
      { key: "aiCopilot", title: "Allow Saathi AI", desc: "Give staff access to Saathi AI on Desktop. Off by default.", inverted: true },
      { key: "store", title: "Block DailyHisaab Store", desc: "Prevent staff from buying hardware + essentials. Owner payment method exposure." },
      { key: "profileView", title: "Show Owner Profile", desc: "Give staff read-only access to business profile (name, email, phone, GSTIN, owner ID). Off by default.", inverted: true },
      { key: "productAdd", title: "Disable Add New Product", desc: "Prevent staff from creating new products." },
      { key: "productEdit", title: "Disable Product Edit", desc: "Prevent staff from editing product details." },
      { key: "productDelete", title: "Allow Delete Product", desc: "Let staff delete products. Off by default.", inverted: true },
      { key: "stockUpdate", title: "Disable Stock Update", desc: "Prevent staff from using Stock Commander." },
      { key: "barcodePrint", title: "Disable Barcode Print", desc: "Prevent staff from printing barcode labels." },
      { key: "transactionPrint", title: "Disable Print Receipt", desc: "Prevent staff from reprinting thermal receipts." },
      { key: "whatsappShare", title: "Disable WhatsApp Invoice", desc: "Prevent staff from sending invoices via WhatsApp." },
    ],
  },
];

const TOGGLEABLE_LOCKS: Array<{ key: LockKey; title: string; desc: string }> =
  LOCK_GROUPS.flatMap((g) => g.items);

export default function UltimateAdmin() {
  const [, setLocation] = useLocation();
  const { plan } = useData();
  const isUltimate = plan?.tier === "ultimate" && plan?.expiresAt && new Date(plan.expiresAt) > new Date();

  if (!isUltimate) {
    return (
      <div className="min-h-screen bg-[#FDFCF7] dark:bg-[#1A1614] p-6">
        <button onClick={() => setLocation("/settings")} className="flex items-center gap-2 text-slate-500 mb-8">
          <ChevronLeft className="w-5 h-5" /> Back
        </button>
        <div className="max-w-md mx-auto text-center mt-20">
          <div className="w-16 h-16 rounded-full bg-amber-50 mx-auto mb-4 flex items-center justify-center">
            <Crown className="w-8 h-8 text-amber-500" />
          </div>
          <h1 className="text-2xl font-black mb-2">Ultimate Plan Required</h1>
          <p className="text-slate-500 text-sm mb-6">Desktop sharing is only available on the Ultimate plan.</p>
          <button onClick={() => setLocation("/subscribe")} className="px-6 py-3 rounded-full bg-[#8B1A1A] text-white font-black uppercase text-xs tracking-widest">Upgrade Now</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF7] dark:bg-[#1A1614] pb-24">
      <header className="sticky top-0 z-20 bg-[#FDFCF7]/95 dark:bg-[#1A1614]/95 backdrop-blur-sm border-b border-slate-100 dark:border-[#454545]">
        <div className="flex items-center gap-3 px-4 py-4">
          <button onClick={() => setLocation("/settings")} className="w-10 h-10 rounded-full bg-white dark:bg-transparent dark:border dark:border-[#454545] flex items-center justify-center">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-lg font-black tracking-tight text-[#0F172A] dark:text-white">Desktop Access</h1>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Manage staff devices</p>
          </div>
        </div>
      </header>

      <StaffAccessPanel />
    </div>
  );
}

// Inline-able body of UltimateAdmin — no header, no back-button.
// Used by UltimateAdmin standalone route AND UltimateSettings page.
export function StaffAccessPanel() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { plan } = useData();

  const [devices, setDevices] = useState<ActiveDevice[]>([]);
  const [staffLocks, setStaffLocks] = useState<StaffLocks>(DEFAULT_STAFF_LOCKS);
  const [activeInvite, setActiveInvite] = useState<{ code: string; expiresAt: string } | null>(null);
  const [inviteTimeLeft, setInviteTimeLeft] = useState<number>(0);
  const [generating, setGenerating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const currentDeviceId = typeof window !== "undefined"
    ? (localStorage.getItem("dh_mobile_device_id") || localStorage.getItem("dh_desktop_device_id") || "")
    : "";
  const [updatingLocks, setUpdatingLocks] = useState<LockKey | null>(null);
  const [copied, setCopied] = useState(false);

  const isUltimate = plan?.tier === "ultimate" && plan?.expiresAt && new Date(plan.expiresAt) > new Date();

  useEffect(() => {
    if (!user) return;
    const ref = doc(db, "users", user.uid);
    let syncedDefaults = false;
    const unsub = onSnapshot(ref, (snap) => {
      const s = snap.data()?.settings || {};
      setDevices(s.devices?.active || []);
      const fbLocks = s.staffLocks || {};
      const merged = { ...DEFAULT_STAFF_LOCKS, ...fbLocks };
      setStaffLocks(merged);
      const firestoreKeys = Object.keys(fbLocks);
      const missingKeys = Object.keys(DEFAULT_STAFF_LOCKS).filter((k) => !firestoreKeys.includes(k));
      if (!syncedDefaults && missingKeys.length > 0) {
        syncedDefaults = true;
        const fn = httpsCallable<{ locks: Partial<StaffLocks> }, { ok: boolean }>(functionsAsia, "updateStaffLocks");
        fn({ locks: merged }).catch(() => {});
      }
      const invites: any[] = s.desktopInvites || [];
      const now = Date.now();
      const fresh = invites
        .filter((i) => !i.usedBy && new Date(i.expiresAt).getTime() > now)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setActiveInvite(fresh[0] ? { code: fresh[0].code, expiresAt: fresh[0].expiresAt } : null);
    });
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!activeInvite) {
      setInviteTimeLeft(0);
      return;
    }
    const tick = () => {
      const secs = Math.max(0, Math.floor((new Date(activeInvite.expiresAt).getTime() - Date.now()) / 1000));
      setInviteTimeLeft(secs);
      if (secs === 0) setActiveInvite(null);
    };
    tick();
    const int = setInterval(tick, 1000);
    return () => clearInterval(int);
  }, [activeInvite]);

  const desktopCount = useMemo(() => devices.filter((d) => d.type === "desktop").length, [devices]);
  const slotsLeft = ULTIMATE_DESKTOP_LIMIT - desktopCount;

  const handleGenerate = async () => {
    if (slotsLeft <= 0) {
      toast({ title: "No slots available", description: "Revoke a device first.", variant: "destructive" });
      return;
    }
    setGenerating(true);
    try {
      const fn = httpsCallable<{}, { ok: boolean; code: string; expiresAt: string }>(functionsAsia, "generateDesktopInvite");
      const res = await fn({});
      setActiveInvite({ code: res.data.code, expiresAt: res.data.expiresAt });
    } catch (err: any) {
      toast({ title: "Generation failed", description: err.message || String(err), variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (device: ActiveDevice) => {
    const isOwnerDevice = device.role !== "staff";
    const msg = isOwnerDevice
      ? `Remove ${device.label}? You'll be signed out on that device and need to log in again to restore access.`
      : `Revoke ${device.label}? Staff will be signed out within seconds.`;
    if (!confirm(msg)) return;
    setRevokingId(device.deviceId);
    try {
      const fn = httpsCallable<{ deviceId: string }, { ok: boolean }>(functionsAsia, "revokeDesktopDevice");
      await fn({ deviceId: device.deviceId });
      toast({ title: "Device revoked", description: `${device.label} removed.` });
    } catch (err: any) {
      toast({ title: "Revoke failed", description: err.message || String(err), variant: "destructive" });
    } finally {
      setRevokingId(null);
    }
  };

  const handleToggleLock = async (key: LockKey, value: boolean) => {
    setUpdatingLocks(key);
    try {
      const fn = httpsCallable<{ locks: Partial<StaffLocks> }, { ok: boolean; staffLocks: StaffLocks }>(functionsAsia, "updateStaffLocks");
      await fn({ locks: { [key]: value } as Partial<StaffLocks> });
      if (user) {
        logAction({
          ownerUid: user.uid,
          action: "settings_change",
          targetId: `staffLock:${key}`,
          meta: { lock: key, value },
        });
      }
    } catch (err: any) {
      toast({ title: "Update failed", description: err.message || String(err), variant: "destructive" });
    } finally {
      setUpdatingLocks(null);
    }
  };

  const copyCode = () => {
    if (!activeInvite) return;
    navigator.clipboard.writeText(activeInvite.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const fmtTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
  const fmtLastSeen = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return `${Math.floor(diff / 86_400_000)}d ago`;
  };

  if (!isUltimate) {
    return (
      <div className="p-6 text-center text-sm text-slate-500">
        Ultimate plan required for staff access controls.
      </div>
    );
  }

  return (
    <main className="px-4 py-6 space-y-6 max-w-md mx-auto">
        {/* Slot counter */}
        <div className="p-5 rounded-[24px] bg-white dark:bg-transparent dark:border dark:border-[#454545] shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Desktop Slots</p>
            <Monitor className="w-4 h-4 text-slate-400" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-black text-[#0F172A] dark:text-white">{desktopCount}</span>
            <span className="text-slate-400 font-bold">/ {ULTIMATE_DESKTOP_LIMIT}</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {slotsLeft > 0 ? `${slotsLeft} slot${slotsLeft > 1 ? "s" : ""} available` : "All slots used — revoke a device to invite another"}
          </p>
        </div>

        {/* Invite section */}
        <div className="p-5 rounded-[24px] bg-white dark:bg-transparent dark:border dark:border-[#454545] shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <KeyRound className="w-4 h-4 text-[#8B1A1A]" />
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Invite Staff</p>
          </div>

          {activeInvite ? (
            <>
              <div className="bg-[#FFF5F2] dark:bg-red-950/20 rounded-2xl p-5 text-center mb-3">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">6-digit code</p>
                <p className="text-4xl font-black tracking-[0.4em] text-[#8B1A1A] mb-2">{activeInvite.code}</p>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Expires in {fmtTime(inviteTimeLeft)}
                </p>
              </div>
              <button
                onClick={copyCode}
                className="w-full h-12 rounded-2xl bg-slate-100 dark:bg-transparent dark:border dark:border-[#454545] text-sm font-bold flex items-center justify-center gap-2"
              >
                {copied ? <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Copied</> : <><Copy className="w-4 h-4" /> Copy Code</>}
              </button>
              <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
                Share this code with your staff. They enter it on the Desktop login screen (click "Use Invite Code Instead").
              </p>
            </>
          ) : (
            <button
              onClick={handleGenerate}
              disabled={generating || slotsLeft <= 0}
              className="w-full h-14 rounded-2xl bg-[#8B1A1A] hover:bg-[#631212] disabled:opacity-40 text-white font-black uppercase tracking-widest text-xs flex items-center justify-center gap-3"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Generate Invite Code</>}
            </button>
          )}
        </div>

        {/* Active devices */}
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Active Devices</p>
          </div>
          {devices.length === 0 ? (
            <div className="p-6 rounded-[24px] bg-white dark:bg-transparent dark:border dark:border-[#454545] text-center text-sm text-slate-500">
              No devices registered yet.
            </div>
          ) : (
            devices.map((d) => {
              const isCurrent = d.deviceId === currentDeviceId;
              return (
                <div key={d.deviceId} className="p-4 rounded-[24px] bg-white dark:bg-transparent dark:border dark:border-[#454545] shadow-sm flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${d.role === "staff" ? "bg-amber-50 dark:bg-amber-950/20" : "bg-emerald-50 dark:bg-emerald-950/20"}`}>
                    {d.role === "staff" ? <Shield className="w-5 h-5 text-amber-600" /> : <ShieldCheck className="w-5 h-5 text-emerald-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-sm text-[#0F172A] dark:text-white truncate">{d.label}</p>
                      {isCurrent && (
                        <span className="rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-[#A8851F] dark:text-[#D4AF37] shrink-0">
                          This Device
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      <span>{d.type === "desktop" ? "Desktop" : "Mobile"}</span>
                      <span>·</span>
                      <span>{d.role === "staff" ? "Staff" : "Owner"}</span>
                      <span>·</span>
                      <span>{fmtLastSeen(d.lastSeenAt)}</span>
                    </div>
                  </div>
                  {!isCurrent && (
                    <button
                      onClick={() => handleRevoke(d)}
                      disabled={revokingId === d.deviceId}
                      className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-950/20 flex items-center justify-center shrink-0 disabled:opacity-40"
                    >
                      {revokingId === d.deviceId ? <Loader2 className="w-4 h-4 animate-spin text-red-500" /> : <Trash2 className="w-4 h-4 text-red-500" />}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Permission toggles */}
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Staff Permissions</p>
          </div>

          {/* Always-on lock notice */}
          <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 dark:text-amber-200 font-medium leading-relaxed">
              Business Profile, Subscription / Billing, Delete Account, Google Drive / Sheets, AI Product Images, and Need Help are <strong>always hidden</strong> on staff devices. These cannot be disabled.
            </p>
          </div>

          {LOCK_GROUPS.map((grp) => (
            <div key={grp.group} className="space-y-3">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 pt-2 px-1">{grp.group}</p>
              {grp.items.map(({ key, title, desc, inverted }) => {
                const raw = !!staffLocks[key];
                const displayed = inverted ? !raw : raw;
                return (
                <div key={key} className="p-4 rounded-[24px] bg-white dark:bg-transparent dark:border dark:border-[#454545] shadow-sm flex items-start gap-3">
                  <div className="flex-1">
                    <p className="font-bold text-sm text-[#0F172A] dark:text-white">{title}</p>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{desc}</p>
                  </div>
                  <div className="shrink-0 pt-1">
                    {updatingLocks === key ? (
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    ) : (
                      <Switch
                        checked={displayed}
                        onCheckedChange={(v) => handleToggleLock(key, inverted ? !v : v)}
                      />
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          ))}
        </div>
      </main>
  );
}
